import {useHydrationDone} from '@aboutyou/lazy-imports';
import {styled} from '@linaria/react';
import React, {CSSProperties} from 'react';
import {
  PreloadableImage,
  SupportedPreloadableImageProps,
} from '../PreloadableImage/PreloadableImage';
import {getRatio, useSrc} from './helpers';
import {Breakpoints, PrimaryResizeSide, Ratio, Widths} from './types';
import {useImageSwapTransition} from './useImageSwapTransition';

export type Props = {
  applyBorderRadius?: boolean;
  /**
   * Generates `sizes` attribute based on a media query and given columns.
   * `columns` represents the number of columns in layout for a specific breakpoint.
   * E.g. `columns: 3` -> image is used in a 3 column layout
   */
  breakpoints: Breakpoints;
  /*
   * Optionally provide some breakpoints that will be used during server-side renderung.
   * You can first load smaller images which will be switched for the normal-quality ones later
   */
  initialBreakpoints?: Breakpoints;
  importance?: 'high' | 'low' | 'auto';
  /**
   * Expects image aspect ratio. Generates the srcSet based on the aspect ratio and the widths, e.g.
   * `srcSet="image_url?width=200&amp;height=300 200w, image_url?width=400&amp;height=600 400w, ..."`
   * Format is width:height
   */
  ratio: Ratio;
  /**
   * Allows to scale the image as needed. By default it will stay to the defined ratio
   */
  disableFixedRatio?: boolean;
  /**
   * Announced by screen readers. Pass "" for decorative images so they are skipped.
   */
  alt: string;

  src: string;
  /**
   * Set `proxyImageSrc` on top of the `src` prop if you want proxy the Image through the current host domain.
   * the `proxyImageSrc` should be using a relative path starting with `/__/cdn-proxy/..` to the current host.
   * eg: `/__/cdn-proxy/file/images/90c5b4e5fbbf37a699b41b7846153a0a.jpg`
   * instead of `https://cdn.aboutstatic.com/file/images/90c5b4e5fbbf37a699b41b7846153a0a.jpg`
   *
   * `proxyImageSrc` would only be used for the initial server rendering. That means it will be used only for the `initialBreakpoints` and not for the other `breakpoints`.
   */
  proxyImageSrc?: string;

  /**
   * Defines (together with ratio prop) the generated sizes/widths for responsive images in srcSet.
   * Most common image widths are pre-set as default and can be overwritten.
   * E.g. widths = `[200, 400]` -> `srcSet="image_url?width=200&amp;height=300 200w, image_url?width=400&amp;height=600 400w"`
   */
  widths?: Widths;

  /**
   * By default width and height are added to the CDN as resize parameter. In some cases this can be unintended, if ratio of the image is unknown and therefore at least one dimension is unknown. If a parameter is given, only that one will be considered for resizing.
   *
   * @type {('width' | 'height' | undefined)}
   */
  primaryResizeSide?: PrimaryResizeSide;
  className?: string;
} & {
  style?: Omit<CSSProperties, 'paddingTop' | 'padding' | 'width' | 'height'>;
} & Omit<
    SupportedPreloadableImageProps,
    'style' | 'sizes' | 'srcSet' | 'importance'
  >;

const DEFAULT_WIDTH = [280, 360, 400, 560, 720, 960, 1280, 1600, 2000];
export const Image = ({
  applyBorderRadius,
  breakpoints,
  initialBreakpoints,
  ratio,
  src,
  proxyImageSrc,
  widths = DEFAULT_WIDTH,
  importance,
  disableFixedRatio,
  primaryResizeSide,
  ref,
  ...props
}: Props & {ref?: React.Ref<HTMLImageElement>}) => {
  const {widthRatio, heightRatio} = getRatio(ratio);
  const hydrationDone = useHydrationDone();

  const shouldUseProxyImage = Boolean(proxyImageSrc && !hydrationDone);

  const {sizes, srcSet, defaultSrc} = useSrc({
    breakpoints:
      !hydrationDone && initialBreakpoints ? initialBreakpoints : breakpoints,
    ratio,
    src: shouldUseProxyImage ? proxyImageSrc! : src,
    widths,
    primaryResizeSide,
  });

  const swapTransition = useImageSwapTransition({
    identity: src,
    source: {src: defaultSrc, srcSet, sizes},
  });

  return (
    <StyledPreloadableImage
      importance={importance}
      $applyBorderRadius={applyBorderRadius}
      $aspectRatio={
        disableFixedRatio ? undefined : `${widthRatio} / ${heightRatio}`
      }
      {...props}
      sizes={swapTransition.source.sizes}
      srcSet={swapTransition.source.srcSet}
      src={swapTransition.source.src}
      style={
        swapTransition.style
          ? {...props.style, ...swapTransition.style}
          : props.style
      }
      ref={ref}
    />
  );
};

const StyledPreloadableImage = styled(PreloadableImage)<{
  $aspectRatio?: string;
  $applyBorderRadius?: boolean;
}>`
  vertical-align: bottom;
  border-radius: ${props => (props.$applyBorderRadius ? '2px' : 0)};
  aspect-ratio: ${props => (props.$aspectRatio ? props.$aspectRatio : 'auto')};
`;
